import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { X, Dog, Cat, ArrowRight, ArrowLeft, Check, Shield, Truck, Warehouse, Gift, Sparkles, MapPin, Tag, ShieldCheck, PackageCheck, CircleDollarSign, BadgeCheck, UserRoundCheck, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import ROMANIAN_LOCALITIES from '../data/romanianLocalities';
import { API } from '../config/api';

// PetsBox logo
const PETSBOX_LOGO = "https://customer-assets.emergentagent.com/job_paws-box/artifacts/gyymiifm_f89199bc-32c1-4646-a02f-5de9041d5b0a.png";
const FAN_COURIER_LOGO = "https://customer-assets.emergentagent.com/job_3bf0b3a7-b977-4048-8ca7-1a63aced161d/artifacts/1rfs9135_fancourierro-logo.webp";

// Partner logos combined image
const PARTNER_LOGOS = "/assets/partner_logos.png";

function CityAutocomplete({ value, onChange, isRomanian }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInput = (val) => {
    setQuery(val);
    onChange({ city: val, county: '', zip: '' });
    if (val.length >= 2) {
      const lower = val.toLowerCase();
      const matches = ROMANIAN_LOCALITIES.filter(l =>
        l.city.toLowerCase().includes(lower) || l.zip.startsWith(val) || l.county.toLowerCase().includes(lower)
      ).slice(0, 8);
      setSuggestions(matches);
      setShowDropdown(matches.length > 0);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  };

  const selectCity = (loc) => {
    setQuery(loc.city);
    setShowDropdown(false);
    onChange(loc);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
        <input
          type="text"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => query.length >= 2 && suggestions.length > 0 && setShowDropdown(true)}
          className="input-field w-full pl-10"
          placeholder={isRomanian ? 'Caută orașul sau codul poștal...' : 'Search city or zip code...'}
          data-testid="city-autocomplete"
        />
      </div>
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-brand-border rounded-xl shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((loc, i) => (
            <button
              key={i}
              onClick={() => selectCity(loc)}
              className="w-full text-left px-4 py-2.5 hover:bg-brand-cream transition-colors flex items-center justify-between text-sm border-b border-brand-border/30 last:border-0"
              data-testid={`city-option-${i}`}
            >
              <span>
                <span className="font-medium text-brand-brown">{loc.city}</span>
                <span className="text-brand-muted ml-1">({loc.county})</span>
              </span>
              <span className="text-xs text-brand-muted font-mono">{loc.zip}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactStep({ data, updateData, t, isRomanian, getBasePrice, getUpgradePrice, totalPrice, couponState, onApplyCoupon, onRemoveCoupon }) {
  const [couponInput, setCouponInput] = useState('');
  
  const handleCitySelect = (loc) => {
    updateData('city', loc.city);
    updateData('county', loc.county);
    updateData('zipCode', loc.zip);
  };

  const handleApplyCoupon = () => {
    if (couponInput.trim()) onApplyCoupon(couponInput.trim());
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-brown mb-2">
          {t('funnel_s7_title')}
        </h2>
        <p className="text-brand-muted">{t('funnel_s7_subtitle')}</p>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-brand-brown mb-2">{t('funnel_name')}</label>
          <input type="text" value={data.name} onChange={(e) => updateData('name', e.target.value)} className="input-field w-full" data-testid="contact-name" />
        </div>
        <div>
          <label className="block text-sm font-medium text-brand-brown mb-2">{t('funnel_phone')}</label>
          <input type="tel" value={data.phone} onChange={(e) => updateData('phone', e.target.value)} className="input-field w-full" placeholder="+40..." data-testid="contact-phone" />
        </div>
        <div>
          <label className="block text-sm font-medium text-brand-brown mb-2">{t('funnel_email')}</label>
          <input type="email" value={data.email} onChange={(e) => updateData('email', e.target.value)} className="input-field w-full" required placeholder="name@example.com" data-testid="contact-email" />
        </div>
        <div>
          <label className="block text-sm font-medium text-brand-brown mb-2">
            {isRomanian ? 'Oraș / Localitate' : 'City / Town'}
          </label>
          <CityAutocomplete value={data.city} onChange={handleCitySelect} isRomanian={isRomanian} />
          {data.city && data.zipCode && (
            <p className="text-xs text-brand-muted mt-1">
              {data.county} • {isRomanian ? 'Cod poștal' : 'Zip'}: {data.zipCode}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-brand-brown mb-2">{t('funnel_address')}</label>
          <textarea
            value={data.address}
            onChange={(e) => updateData('address', e.target.value)}
            className="input-field w-full resize-none"
            rows={2}
            placeholder={isRomanian ? 'Strada, număr, bloc, scară, apartament...' : 'Street, number, building, apartment...'}
            data-testid="contact-address"
          />
        </div>
      </div>

      {/* Coupon Code */}
      <div className="bg-brand-cream/60 rounded-2xl p-4">
        <label className="block text-sm font-medium text-brand-brown mb-2">
          <Tag className="w-4 h-4 inline mr-1" />
          {isRomanian ? 'Cod promoțional' : 'Promo code'}
        </label>
        {couponState.applied ? (
          <div className="flex items-center justify-between bg-brand-success/10 border border-brand-success/30 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-brand-success" />
              <span className="font-bold text-brand-success text-sm">{couponState.code}</span>
              <span className="text-xs text-brand-muted">
                (-{couponState.discount} {isRomanian ? 'lei' : '€'})
              </span>
            </div>
            <button onClick={onRemoveCoupon} className="text-xs text-red-500 hover:underline" data-testid="remove-coupon-btn">
              {isRomanian ? 'Elimină' : 'Remove'}
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              type="text"
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              placeholder={isRomanian ? 'Introdu codul...' : 'Enter code...'}
              className="input-field flex-1"
              data-testid="coupon-input"
            />
            <button
              onClick={handleApplyCoupon}
              disabled={!couponInput.trim() || couponState.loading}
              className="bg-brand-brown text-white px-5 py-2 rounded-xl text-sm font-semibold hover:bg-brand-brown/90 transition-colors disabled:opacity-50"
              data-testid="apply-coupon-btn"
            >
              {couponState.loading ? '...' : isRomanian ? 'Aplică' : 'Apply'}
            </button>
          </div>
        )}
        {couponState.error && (
          <p className="text-xs text-red-500 mt-2">{couponState.error}</p>
        )}
      </div>

      {/* Payment Method Selector */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-brand-brown">
          {isRomanian ? 'Metoda de plată' : 'Payment method'}
        </label>
        <div
          onClick={() => updateData('paymentMethod', 'card')}
          className={`relative cursor-pointer rounded-2xl border-2 p-4 transition-all ${
            data.paymentMethod === 'card'
              ? 'border-brand-orange bg-gradient-to-r from-brand-orange/5 to-brand-cream shadow-md'
              : 'border-brand-border hover:border-brand-orange/40'
          }`}
          data-testid="payment-card"
        >
          {data.paymentMethod === 'card' && (
            <div className="absolute -top-2.5 left-4 bg-brand-orange text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
              {isRomanian ? 'Recomandat' : 'Recommended'}
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-orange to-amber-500 flex items-center justify-center shadow-sm">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-bold text-brand-brown">
                  {isRomanian ? 'Plată cu cardul' : 'Card payment'}
                </p>
                <p className="text-xs text-brand-muted">
                  {isRomanian ? 'Livrare GRATUITĂ — anulezi oricând din cont' : 'FREE delivery — cancel anytime from your account'}
                </p>
              </div>
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              data.paymentMethod === 'card' ? 'border-brand-orange bg-brand-orange' : 'border-brand-border'
            }`}>
              {data.paymentMethod === 'card' && <Check className="w-3 h-3 text-white" />}
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs text-brand-success bg-brand-success/10 px-2.5 py-1 rounded-full">
              <BadgeCheck className="w-3 h-3" />
              {isRomanian ? 'Plată securizată Stripe' : 'Secure Stripe payment'}
            </div>
            <div className="flex items-center gap-2 bg-gray-100 px-2.5 py-1 rounded-full">
              <svg viewBox="0 0 24 24" className="h-3.5 w-auto" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.32 2.32-1.55 4.3-3.74 4.25z"/></svg>
              <span className="text-[10px] font-semibold text-gray-600">Apple Pay</span>
              <svg viewBox="0 0 24 24" className="h-3.5 w-auto" fill="#5f6368"><path d="M21.35 11.1h-9.18v2.73h5.51c-.24 1.27-.97 2.34-2.06 3.07v2.55h3.33c1.95-1.79 3.07-4.43 3.07-7.55 0-.52-.05-1.03-.14-1.53l-.53.73z"/><path d="M12.17 22c2.78 0 5.11-.92 6.82-2.5l-3.33-2.55c-.92.62-2.1.99-3.49.99-2.69 0-4.96-1.82-5.78-4.26H2.96v2.63A10.18 10.18 0 0012.17 22z" fill="#34a853"/><path d="M6.39 14.09a5.95 5.95 0 010-3.82V7.64H2.96a10.18 10.18 0 000 9.08l3.43-2.63z" fill="#fbbc05"/><path d="M12.17 5.98c1.52 0 2.88.52 3.95 1.55l2.96-2.96C17.27 2.84 14.94 1.82 12.17 1.82A10.18 10.18 0 002.96 7.64l3.43 2.63c.82-2.44 3.09-4.29 5.78-4.29z" fill="#ea4335"/></svg>
              <span className="text-[10px] font-semibold text-gray-600">Google Pay</span>
            </div>
          </div>
        </div>
        <div
          onClick={() => updateData('paymentMethod', 'ramburs')}
          className={`relative cursor-pointer rounded-2xl border-2 p-4 transition-all ${
            data.paymentMethod === 'ramburs'
              ? 'border-brand-orange bg-brand-orange/5'
              : 'border-brand-border hover:border-brand-orange/40'
          }`}
          data-testid="payment-ramburs"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Truck className="w-5 h-5 text-brand-orange" />
              <div>
                <p className="font-semibold text-brand-brown text-sm">
                  {isRomanian ? 'Plată prin ramburs' : 'Cash on delivery'}
                </p>
                <p className="text-xs text-brand-muted">
                  {isRomanian ? 'Plătești curierului la livrare' : 'Pay the courier on delivery'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                +{isRomanian ? '11 RON' : '2€'}
              </span>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                data.paymentMethod === 'ramburs' ? 'border-brand-orange bg-brand-orange' : 'border-brand-border'
              }`}>
                {data.paymentMethod === 'ramburs' && <Check className="w-3 h-3 text-white" />}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-brand-cream rounded-2xl p-4 space-y-3">
        <h3 className="font-heading font-semibold text-brand-brown">{t('funnel_summary')}</h3>
        <div className="flex justify-between text-sm">
          <span className="text-brand-muted">{t('funnel_monthly_sub')}</span>
          <span className="font-medium">{getBasePrice()} {isRomanian ? 'lei' : '€'}</span>
        </div>
        {data.wantsUpgrade && (
          <div className="flex justify-between text-sm">
            <span className="text-brand-muted">{t('funnel_upgrade_label')}</span>
            <span className="font-medium">+{getUpgradePrice()} {isRomanian ? 'lei' : '€'}</span>
          </div>
        )}
        {couponState.applied && (
          <div className="flex justify-between text-sm text-brand-success">
            <span>{isRomanian ? 'Reducere' : 'Discount'} ({couponState.code})</span>
            <span>-{couponState.discount} {isRomanian ? 'lei' : '€'}</span>
          </div>
        )}
        {data.paymentMethod === 'card' ? (
          <div className="flex justify-between text-sm text-brand-success">
            <span>{t('funnel_free_delivery')}</span>
            <span>0 {isRomanian ? 'lei' : '€'}</span>
          </div>
        ) : (
          <div className="flex justify-between text-sm text-brand-success">
            <span className="flex items-center gap-1.5">
              <img src={FAN_COURIER_LOGO} alt="FAN Courier / DPD Romania" className="h-4 w-4 object-contain" />
              {isRomanian ? 'Livrare GRATUITĂ (ofertă vară)' : 'FREE shipping (Summer offer)'}
            </span>
            <span>0 {isRomanian ? 'lei' : '€'}</span>
          </div>
        )}
        {data.paymentMethod === 'ramburs' && (
          <div className="flex justify-between text-sm text-brand-muted">
            <span>{isRomanian ? 'Taxă ramburs' : 'COD fee'}</span>
            <span>+{isRomanian ? '11 lei' : '2€'}</span>
          </div>
        )}
        <div className="border-t border-brand-border pt-3 flex justify-between">
          <span className="font-heading font-bold text-brand-brown">{t('funnel_total')}</span>
          <span className="font-heading font-bold text-brand-orange text-xl">
            {totalPrice - (couponState.applied ? couponState.discount : 0) + (data.paymentMethod === 'ramburs' ? (isRomanian ? 11 : 2) : 0)} {isRomanian ? 'lei' : '€'}/{isRomanian ? 'lună' : 'month'}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function SubscribeFunnel({ isOpen, onClose }) {
  const { t, isRomanian, getBasePrice, getUpgradePrice } = useLanguage();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [couponState, setCouponState] = useState({ applied: false, code: '', discount: 0, loading: false, error: '' });
  const [data, setData] = useState({
    petType: '', // 'dog' or 'cat'
    dogSize: '', // 'small', 'medium', 'large'
    destroysToys: null, // true/false
    petName: '',
    petGender: '', // 'boy' or 'girl'
    breed: '',
    age: '',
    allergies: ['none'], // can contain: 'chicken', 'beef', 'other', 'none'
    allergyOther: '',
    wantsUpgrade: false,
    name: '',
    phone: '',
    email: '',
    city: '',
    county: '',
    zipCode: '',
    address: '',
    paymentMethod: 'card',
  });

  const totalSteps = data.petType === 'dog' ? 7 : 6;

  // Track funnel opens
  useEffect(() => {
    if (isOpen) {
      fetch(`${API}/analytics/funnel-start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }).catch(() => {});
    }
  }, [isOpen]);

  // Save abandoned cart as soon as email is entered
  useEffect(() => {
    if (!isOpen || !data.email) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) return;
    const timeout = setTimeout(() => {
      fetch(`${API}/cart/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          name: data.name || null,
          petName: data.petName || null,
          petType: data.petType || null,
          phone: data.phone || null,
          funnel_step: step,
        }),
      }).catch(() => {});
    }, 2000);
    return () => clearTimeout(timeout);
  }, [isOpen, data.email, data.name, step, data.petName, data.petType, data.phone]);

  if (!isOpen) return null;

  const applyCoupon = async (code) => {
    setCouponState(s => ({ ...s, loading: true, error: '' }));
    try {
      const res = await fetch(`${API}/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, currency: isRomanian ? 'RON' : 'EUR', email: data.email }),
      });
      if (!res.ok) {
        const err = await res.json();
        setCouponState({ applied: false, code: '', discount: 0, loading: false, error: err.detail || (isRomanian ? 'Cod invalid' : 'Invalid code') });
        return;
      }
      const result = await res.json();
      setCouponState({ applied: true, code: result.code, discount: result.discount_amount, loading: false, error: '' });
      toast.success(isRomanian ? 'Cod promoțional aplicat!' : 'Coupon applied!');
    } catch {
      setCouponState(s => ({ ...s, loading: false, error: isRomanian ? 'Eroare la verificarea codului' : 'Error verifying code' }));
    }
  };

  const removeCoupon = () => {
    setCouponState({ applied: false, code: '', discount: 0, loading: false, error: '' });
  };

  const updateData = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAllergy = (allergy) => {
    setData(prev => {
      let newAllergies = [...prev.allergies];
      
      if (allergy === 'none') {
        // If selecting 'none', clear all others
        newAllergies = ['none'];
      } else {
        // Remove 'none' if selecting another allergy
        newAllergies = newAllergies.filter(a => a !== 'none');
        
        if (newAllergies.includes(allergy)) {
          newAllergies = newAllergies.filter(a => a !== allergy);
        } else {
          newAllergies.push(allergy);
        }
        
        // If no allergies selected, default to 'none'
        if (newAllergies.length === 0) {
          newAllergies = ['none'];
        }
      }
      
      return { ...prev, allergies: newAllergies };
    });
  };

  const canContinue = () => {
    switch (step) {
      case 1: return !!data.petType;
      case 2: 
        if (data.petType === 'dog') {
          return !!data.dogSize && data.destroysToys !== null;
        }
        return true;
      case 3: return !!data.petName && !!data.petGender;
      case 4: return true; // breed and age are optional
      case 5: return data.allergies.length > 0;
      case 6: return true; // upgrade is optional
      case 7: {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !!data.name && !!data.phone && !!data.email && emailRegex.test(data.email) && !!data.city && !!data.address;
      }
      default: return false;
    }
  };

  const getActualStep = () => {
    // For cats, skip step 2 (dog size)
    if (data.petType === 'cat' && step > 1) {
      return step + 1;
    }
    return step;
  };

  const nextStep = () => {
    if (data.petType === 'cat' && step === 1) {
      setStep(3); // Skip dog size step for cats
    } else {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (data.petType === 'cat' && step === 3) {
      setStep(1); // Go back to pet type from step 3 for cats
    } else {
      setStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const orderData = {
        petType: data.petType,
        dogSize: data.dogSize || null,
        destroysToys: data.destroysToys,
        petName: data.petName,
        petGender: data.petGender,
        breed: data.breed || null,
        age: data.age || null,
        allergies: data.allergies,
        allergyOther: data.allergyOther || null,
        wantsUpgrade: data.wantsUpgrade,
        name: data.name,
        phone: data.phone,
        email: data.email,
        city: data.city || null,
        county: data.county || null,
        zipCode: data.zipCode || null,
        address: data.address,
        currency: isRomanian ? 'RON' : 'EUR',
        originUrl: window.location.origin,
        couponCode: couponState.applied ? couponState.code : null,
        paymentMethod: data.paymentMethod,
      };

      // Use XMLHttpRequest to bypass PostHog fetch interception
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/orders/checkout`);
        xhr.withCredentials = true;
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            let errMsg = isRomanian ? 'Eroare la trimiterea comenzii' : 'Error submitting order';
            try {
              const err = JSON.parse(xhr.responseText);
              if (err.detail) errMsg = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
            } catch {}
            reject(new Error(errMsg));
          }
        };
        xhr.onerror = () => reject(new Error(isRomanian ? 'Eroare de conexiune. Încearcă din nou.' : 'Connection error. Try again.'));
        xhr.send(JSON.stringify(orderData));
      });

      // Calculate actual charged amount
      const finalAmount = totalPrice - (couponState.applied ? couponState.discount : 0) + (data.paymentMethod === 'ramburs' ? (isRomanian ? 11 : 2) : 0);

      // Fire Meta Pixel InitiateCheckout event
      if (window.fbq) {
        window.fbq('track', 'InitiateCheckout', {
          value: finalAmount,
          currency: isRomanian ? 'RON' : 'EUR',
          content_name: 'PetsBox Subscription',
          content_category: data.petType,
        });
      }
      // Redirect to Stripe Checkout
      if (result.url) {
        window.location.href = result.url;
      } else {
        toast.error(isRomanian ? 'Eroare la plata' : 'Payment error');
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error?.message || (isRomanian ? 'Eroare la trimiterea comenzii' : 'Error submitting order'));
    } finally {
      setLoading(false);
    }
  };

  const totalPrice = getBasePrice() + (data.wantsUpgrade ? getUpgradePrice() : 0);

  const renderStep = () => {
    const actualStep = data.petType === 'cat' && step > 1 ? step : step;
    
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-brown mb-2">
                {t('funnel_s1_title')}
              </h2>
              <p className="text-brand-muted">{t('funnel_s1_subtitle')}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => updateData('petType', 'dog')}
                className={`p-8 rounded-2xl border-2 transition-all ${
                  data.petType === 'dog' 
                    ? 'border-brand-orange bg-brand-orange/10' 
                    : 'border-brand-border hover:border-brand-orange/50'
                }`}
                data-testid="pet-type-dog"
              >
                <Dog className={`w-16 h-16 mx-auto mb-4 ${data.petType === 'dog' ? 'text-brand-orange' : 'text-brand-brown'}`} />
                <p className="font-heading text-xl font-semibold text-brand-brown">{t('funnel_dog')}</p>
              </button>
              
              <button
                onClick={() => updateData('petType', 'cat')}
                className={`p-8 rounded-2xl border-2 transition-all ${
                  data.petType === 'cat' 
                    ? 'border-brand-orange bg-brand-orange/10' 
                    : 'border-brand-border hover:border-brand-orange/50'
                }`}
                data-testid="pet-type-cat"
              >
                <Cat className={`w-16 h-16 mx-auto mb-4 ${data.petType === 'cat' ? 'text-brand-orange' : 'text-brand-brown'}`} />
                <p className="font-heading text-xl font-semibold text-brand-brown">{t('funnel_cat')}</p>
              </button>
            </div>
          </div>
        );

      case 2:
        // Dog size step (only for dogs)
        if (data.petType !== 'dog') {
          nextStep();
          return null;
        }
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-brown mb-2">
                {t('funnel_s2_title')}
              </h2>
              <p className="text-brand-muted">{t('funnel_s2_subtitle')}</p>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              {['small', 'medium', 'large'].map(size => (
                <button
                  key={size}
                  onClick={() => updateData('dogSize', size)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    data.dogSize === size 
                      ? 'border-brand-orange bg-brand-orange/10' 
                      : 'border-brand-border hover:border-brand-orange/50'
                  }`}
                  data-testid={`dog-size-${size}`}
                >
                  <p className="text-3xl mb-2">{size === 'small' ? '🐕' : size === 'medium' ? '🐕‍🦺' : '🦮'}</p>
                  <p className="font-medium text-brand-brown text-sm">{t(`funnel_size_${size}`)}</p>
                </button>
              ))}
            </div>

            <div className="space-y-3">
              <p className="font-medium text-brand-brown">{t('funnel_destroys')}</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => updateData('destroysToys', true)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    data.destroysToys === true 
                      ? 'border-brand-orange bg-brand-orange/10' 
                      : 'border-brand-border hover:border-brand-orange/50'
                  }`}
                  data-testid="destroys-yes"
                >
                  <p className="font-medium text-brand-brown">{t('funnel_destroys_yes')}</p>
                </button>
                <button
                  onClick={() => updateData('destroysToys', false)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    data.destroysToys === false 
                      ? 'border-brand-orange bg-brand-orange/10' 
                      : 'border-brand-border hover:border-brand-orange/50'
                  }`}
                  data-testid="destroys-no"
                >
                  <p className="font-medium text-brand-brown">{t('funnel_destroys_no')}</p>
                </button>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-brown mb-2">
                {t('funnel_s3_title')}
              </h2>
              <p className="text-brand-muted">{t('funnel_s3_subtitle')}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-brand-brown mb-2">{t('funnel_pet_name')}</label>
              <input
                type="text"
                value={data.petName}
                onChange={(e) => updateData('petName', e.target.value)}
                className="input-field w-full text-lg"
                placeholder={data.petType === 'dog' ? 'Max, Bella...' : 'Luna, Milo...'}
                data-testid="pet-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-brown mb-2">{t('funnel_gender')}</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => updateData('petGender', 'boy')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    data.petGender === 'boy' 
                      ? 'border-brand-orange bg-brand-orange/10' 
                      : 'border-brand-border hover:border-brand-orange/50'
                  }`}
                  data-testid="gender-boy"
                >
                  <p className="text-2xl mb-1">♂️</p>
                  <p className="font-medium text-brand-brown">{t('funnel_boy')}</p>
                </button>
                <button
                  onClick={() => updateData('petGender', 'girl')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    data.petGender === 'girl' 
                      ? 'border-brand-orange bg-brand-orange/10' 
                      : 'border-brand-border hover:border-brand-orange/50'
                  }`}
                  data-testid="gender-girl"
                >
                  <p className="text-2xl mb-1">♀️</p>
                  <p className="font-medium text-brand-brown">{t('funnel_girl')}</p>
                </button>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-brown mb-2">
                {t('funnel_s4_title')}
              </h2>
              <p className="text-brand-muted">{t('funnel_s4_subtitle')}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-brand-brown mb-2">{t('funnel_breed')}</label>
              <input
                type="text"
                value={data.breed}
                onChange={(e) => updateData('breed', e.target.value)}
                className="input-field w-full"
                placeholder={t('funnel_breed_placeholder')}
                data-testid="breed-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-brown mb-2">{t('funnel_age')}</label>
              <input
                type="text"
                value={data.age}
                onChange={(e) => updateData('age', e.target.value)}
                className="input-field w-full"
                placeholder={t('funnel_age_placeholder')}
                data-testid="age-input"
              />
              <button 
                onClick={() => updateData('age', t('funnel_dont_know'))}
                className="text-sm text-brand-orange mt-2 hover:underline"
              >
                {t('funnel_dont_know')}
              </button>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-brown mb-2">
                {t('funnel_s5_title')}
              </h2>
              <p className="text-brand-muted">{t('funnel_s5_subtitle')}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {['chicken', 'beef', 'other', 'none'].map(allergy => (
                <button
                  key={allergy}
                  onClick={() => toggleAllergy(allergy)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    data.allergies.includes(allergy) 
                      ? 'border-brand-orange bg-brand-orange/10' 
                      : 'border-brand-border hover:border-brand-orange/50'
                  }`}
                  data-testid={`allergy-${allergy}`}
                >
                  <p className="text-xl mb-1">
                    {allergy === 'chicken' ? '🍗' : allergy === 'beef' ? '🥩' : allergy === 'other' ? '❓' : '✅'}
                  </p>
                  <p className="font-medium text-brand-brown text-sm">{t(`funnel_allergy_${allergy}`)}</p>
                  {data.allergies.includes(allergy) && (
                    <Check className="w-4 h-4 text-brand-orange mx-auto mt-1" />
                  )}
                </button>
              ))}
            </div>

            {data.allergies.includes('other') && (
              <input
                type="text"
                value={data.allergyOther}
                onChange={(e) => updateData('allergyOther', e.target.value)}
                className="input-field w-full"
                placeholder={t('funnel_allergy_other_placeholder')}
                data-testid="allergy-other-input"
              />
            )}
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-brown mb-2">
                {t('funnel_s6_title')}
              </h2>
              <p className="text-brand-muted">{t('funnel_s6_subtitle')}</p>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <button
                onClick={() => updateData('wantsUpgrade', true)}
                className={`p-6 rounded-2xl border-2 transition-all text-left ${
                  data.wantsUpgrade 
                    ? 'border-brand-orange bg-brand-orange/10' 
                    : 'border-brand-border hover:border-brand-orange/50'
                }`}
                data-testid="upgrade-yes"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-brand-orange" />
                    <p className="font-heading text-lg font-semibold text-brand-brown">{t('funnel_upgrade_yes')}</p>
                  </div>
                  <span className="bg-brand-orange text-white px-3 py-1 rounded-full text-sm font-bold">
                    {t('funnel_upgrade_price')}
                  </span>
                </div>
                <p className="text-sm text-brand-muted">{t('funnel_upgrade_benefits')}</p>
              </button>
              
              <button
                onClick={() => updateData('wantsUpgrade', false)}
                className={`p-6 rounded-2xl border-2 transition-all text-left ${
                  !data.wantsUpgrade 
                    ? 'border-brand-orange bg-brand-orange/10' 
                    : 'border-brand-border hover:border-brand-orange/50'
                }`}
                data-testid="upgrade-no"
              >
                <p className="font-heading text-lg font-semibold text-brand-brown">{t('funnel_upgrade_no')}</p>
              </button>
            </div>
          </div>
        );

      case 7:
        return <ContactStep data={data} updateData={updateData} t={t} isRomanian={isRomanian} getBasePrice={getBasePrice} getUpgradePrice={getUpgradePrice} totalPrice={totalPrice} couponState={couponState} onApplyCoupon={applyCoupon} onRemoveCoupon={removeCoupon} />;

      default:
        return null;
    }
  };

  const displayStep = data.petType === 'cat' && step > 1 ? step - 1 : step;
  const displayTotalSteps = data.petType === 'cat' ? 6 : 7;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="subscribe-funnel">
      <div className="absolute inset-0 bg-brand-cream" />
      
      <div className="relative bg-white w-full h-full max-w-2xl mx-auto lg:my-4 lg:h-auto lg:max-h-[95vh] lg:rounded-3xl overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-brand-border px-6 py-4 flex items-center justify-between z-10 lg:rounded-t-3xl">
          <div className="flex items-center gap-3">
            <img src={PETSBOX_LOGO} alt="PetsBox" className="w-10 h-10 rounded-xl" />
            <div>
              <p className="text-xs text-brand-muted">{t('funnel_step')} {displayStep} {t('funnel_of')} {displayTotalSteps}</p>
              <div className="flex gap-1 mt-1">
                {Array.from({ length: displayTotalSteps }).map((_, i) => (
                  <div 
                    key={i} 
                    className={`h-1.5 w-8 rounded-full transition-all ${
                      i < displayStep ? 'bg-brand-orange' : 'bg-brand-border'
                    }`} 
                  />
                ))}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-brand-muted hover:text-brand-brown" data-testid="close-funnel">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Summer promo banner */}
        <div className="bg-brand-orange text-white text-center py-2 px-4 text-sm font-bold tracking-wide">
          {isRomanian ? '🇷🇴 OFERTĂ VARĂ: Livrare GRATUITĂ PE VIAȚĂ!' : '🇷🇴 SUMMER OFFER: FREE DELIVERY FOR LIFE!'}
        </div>

        {/* Social proof strip */}
        <div className="bg-brand-cream/70 border-b border-brand-border/50 px-4 py-2.5 flex items-center justify-center gap-4 flex-wrap text-xs">
          <div className="flex items-center gap-1.5 text-brand-brown font-semibold">
            <PackageCheck className="w-3.5 h-3.5 text-brand-orange" />
            5100+ {isRomanian ? 'cutii trimise' : 'boxes sent'}
          </div>
          <div className="flex items-center gap-1.5 text-brand-brown">
            <ShieldCheck className="w-3.5 h-3.5 text-brand-success" />
            {isRomanian ? 'Garanție ramburs' : 'Money-back guarantee'}
          </div>
          <div className="flex items-center gap-1.5 text-brand-brown">
            <UserRoundCheck className="w-3.5 h-3.5 text-brand-orange" />
            {isRomanian ? 'Anulezi oricând' : 'Cancel anytime'}
          </div>
          <div className="flex items-center gap-1.5 text-brand-brown">
            <Truck className="w-3.5 h-3.5 text-amber-600" />
            {isRomanian ? 'Plata prin ramburs' : 'Cash on delivery'}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex-1">
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-brand-border px-6 py-4 flex items-center justify-between lg:rounded-b-3xl">
          {step > 1 ? (
            <button 
              onClick={prevStep}
              className="flex items-center gap-2 text-brand-muted hover:text-brand-brown transition-colors"
              data-testid="funnel-back"
            >
              <ArrowLeft className="w-5 h-5" />
              {t('funnel_back')}
            </button>
          ) : (
            <div></div>
          )}
          
          {step < 7 ? (
            <button
              onClick={nextStep}
              disabled={!canContinue()}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                canContinue() 
                  ? 'bg-brand-orange text-white hover:bg-brand-orange-hover' 
                  : 'bg-brand-border text-brand-muted cursor-not-allowed'
              }`}
              data-testid="funnel-next"
            >
              {t('funnel_next')}
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canContinue() || loading}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                canContinue() && !loading
                  ? 'bg-brand-orange text-white hover:bg-brand-orange-hover' 
                  : 'bg-brand-border text-brand-muted cursor-not-allowed'
              }`}
              data-testid="funnel-submit"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : (
                <>
                  {data.paymentMethod === 'ramburs'
                    ? (isRomanian ? 'Plasează comanda' : 'Place order')
                    : t('funnel_pay')
                  }
                  <Check className="w-5 h-5" />
                </>
              )}
            </button>
          )}
        </div>

        {/* Trust badges in footer */}
        <div className="px-6 pb-4 space-y-2.5">
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
            <div className="flex items-center gap-1.5 bg-brand-success/10 text-brand-success px-2.5 py-1 rounded-full font-medium">
              <ShieldCheck className="w-3 h-3" />
              {t('trust_secure')}
            </div>
            <div className="flex items-center gap-1.5 bg-brand-orange/10 text-brand-orange px-2.5 py-1 rounded-full font-medium">
              <Truck className="w-3 h-3" />
              {t('trust_shipping')}
            </div>
            <div className="flex items-center gap-1.5 bg-brand-success/10 text-brand-success px-2.5 py-1 rounded-full font-medium">
              <CircleDollarSign className="w-3 h-3" />
              {isRomanian ? 'Garanție ramburs' : 'Money-back'}
            </div>
            <div className="flex items-center gap-1.5 bg-brand-cream px-2.5 py-1 rounded-full text-brand-brown font-medium">
              {'\u{1F1F7}\u{1F1F4}'} {'\u{1F1E7}\u{1F1EC}'}
            </div>
          </div>
          {/* Courier & Payment Logos */}
          <div className="flex items-center justify-center">
            <img src={PARTNER_LOGOS} alt="DPD Romania, Visa, Mastercard" className="h-7 object-contain opacity-70" />
          </div>
        </div>
      </div>
    </div>
  );
}
